import { useEffect, useMemo, useState } from 'react';
import type { Column, Rec } from '../api';
import { computeSheet, isFormula, isVolatile, type Computed, type SheetContext } from './engine';

/**
 * Оптимизированный хук вычисления формул:
 * - Один проход для проверки наличия формул и летучих функций
 * - Мгновенный возврат пустой Map, если в таблице нет формул
 * - Таймер запускается строго при наличии летучих функций (СЕГОДНЯ, СЛЧИС)
 * - При наличии allSheets поддерживает межстраничные формулы
 */
export function useComputed(
  columns: Column[],
  records: Rec[],
  allSheets?: SheetContext[],
  activeSheetName?: string,
): Map<string, Computed> {
  const [tick, setTick] = useState(0);

  const { hasFormulas, hasVolatile } = useMemo(() => {
    if (!records.length || !columns.length) {
      return { hasFormulas: false, hasVolatile: false };
    }

    let formulas = false;
    let volatile = false;

    for (let r = 0; r < records.length; r++) {
      const fields = records[r].fields;
      for (let c = 0; c < columns.length; c++) {
        const v = fields[columns[c].fields.colId];
        if (isFormula(v)) {
          formulas = true;
          if (isVolatile(v)) {
            volatile = true;
            return { hasFormulas: true, hasVolatile: true };
          }
        }
      }
    }

    return { hasFormulas: formulas, hasVolatile: volatile };
  }, [records, columns]);

  useEffect(() => {
    if (!hasVolatile) return;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [hasVolatile]);

  return useMemo(() => {
    if (!hasFormulas) return new Map<string, Computed>();
    try {
      return computeSheet(columns, records, allSheets, activeSheetName);
    } catch {
      // Исключение не должно ронять рендеринг таблицы
      return new Map<string, Computed>();
    }
  }, [columns, records, allSheets, activeSheetName, tick, hasFormulas]);
}
